import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import { exportToExcel, exportToPdf } from './generalLedgerTrialBalanceExports';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './GeneralLedgerTrialBalancePage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    forbidden: 'Bu sirket icin Kebir Mizan uretme yetkiniz yok.',
    fetchFailed: 'Kebir mizan verisi alinamadi.',
    fetchError: 'Kebir mizan verileri alinirken hata olustu.',
    generateFailed: 'Kebir mizan uretimi sirasinda hata olustu.',
    headerTitle: 'Kebir Mizan Intelligence',
    companyNotSelected: 'Sirket secilmedi',
    periodNotSelected: 'Donem secilmedi',
    headerDescription: '{{company}} | {{period}} icin Kebir Mizan uretimini yonetin ve sonuclari inceleyin.',
    exportHeader: '{{company}} | {{period}} icin Kebir Mizan.',
    source: 'Kaynak',
    sourceTB: 'TB (trial_balance_entries)',
    sourceJournal: 'JOURNAL (accounting_voucher_lines)',
    sourceIncome: 'Gelir Tablosu (TB + ters bakiye + PNL yansitma, kapanis haric)',
    sourceCorrected: 'Duzeltilmis / Bilanco (TB + ters bakiye + PNL + kapanis fisleri)',
    generating: 'Uretiliyor...',
    generateRefresh: 'Uret / Yenile',
    exportExcelTitle: 'Excel olarak indir',
    exportPdfTitle: 'PDF olarak indir',
    balanceScore: 'Denge Skoru',
    balanced: 'Dengeli',
    hasDiff: 'Fark var',
    totalDebit: 'Toplam Borc',
    debitTotalNote: 'Borc hareket toplami',
    totalCredit: 'Toplam Alacak',
    creditTotalNote: 'Alacak hareket toplami',
    diff: 'Fark',
    trialEqual: 'Mizan esit',
    reconciliationRequired: 'Mutabakat gerekli',
    lastGeneration: 'Son Uretim',
    rowCount: 'Satir',
    outputTable: 'Cikti Tablosu',
    rowsDisplayed: '{{count}} satir goruntulendi',
    loadingData: 'Veriler yukleniyor...',
    emptyData: 'Bu kombinasyon icin kebir mizan uretimi bulunamadi.',
    colKebirCode: 'Kebir Kod',
    colKebirName: 'Kebir Ad',
    colDebit: 'Borc',
    colCredit: 'Alacak',
    colBalance: 'Bakiye',
    colRows: 'Satir',
    prev: 'Onceki',
    next: 'Sonraki',
    pageSummary: 'Sayfa {{page}} / {{totalPages}} | Toplam {{totalItems}} satir',
    accountCodeFilterLabel: 'Hesap Kodu Filtresi',
    accountCodeFilterPlaceholder: '254 veya 25* / 25%',
    filteredLabel: 'Filtreli'
  },
  'en-US': {
    forbidden: 'You do not have permission to generate General Ledger Trial Balance for this company.',
    fetchFailed: 'General ledger trial balance data could not be fetched.',
    fetchError: 'An error occurred while fetching general ledger trial balance data.',
    generateFailed: 'An error occurred while generating general ledger trial balance.',
    headerTitle: 'General Ledger Trial Balance Intelligence',
    companyNotSelected: 'Company not selected',
    periodNotSelected: 'Period not selected',
    headerDescription: 'Manage generation and review results for {{company}} | {{period}}.',
    exportHeader: 'General Ledger Trial Balance for {{company}} | {{period}}.',
    source: 'Source',
    sourceTB: 'TB (trial_balance_entries)',
    sourceJournal: 'JOURNAL (accounting_voucher_lines)',
    sourceIncome: 'Income Statement (TB + reverse balance + PNL allocation, excluding closing)',
    sourceCorrected: 'Corrected / Balance Sheet (TB + reverse balance + PNL + closing vouchers)',
    generating: 'Generating...',
    generateRefresh: 'Generate / Refresh',
    exportExcelTitle: 'Download as Excel',
    exportPdfTitle: 'Download as PDF',
    balanceScore: 'Balance Score',
    balanced: 'Balanced',
    hasDiff: 'Difference exists',
    totalDebit: 'Total Debit',
    debitTotalNote: 'Total debit movement',
    totalCredit: 'Total Credit',
    creditTotalNote: 'Total credit movement',
    diff: 'Difference',
    trialEqual: 'Trial balanced',
    reconciliationRequired: 'Reconciliation required',
    lastGeneration: 'Last Generation',
    rowCount: 'Rows',
    outputTable: 'Output Table',
    rowsDisplayed: '{{count}} rows displayed',
    loadingData: 'Loading data...',
    emptyData: 'No general ledger trial balance generated for this combination.',
    colKebirCode: 'Ledger Code',
    colKebirName: 'Ledger Name',
    colDebit: 'Debit',
    colCredit: 'Credit',
    colBalance: 'Balance',
    colRows: 'Rows',
    prev: 'Previous',
    next: 'Next',
    pageSummary: 'Page {{page}} / {{totalPages}} | Total {{totalItems}} rows',
    accountCodeFilterLabel: 'Account Code Filter',
    accountCodeFilterPlaceholder: '254 or 25* / 25%',
    filteredLabel: 'Filtered'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const parseErrorMessage = async (response, fallback, t) => {
  if (response.status === 403) return t('forbidden');
  try {
    const payload = await response.json();
    if (payload?.message) return payload.message;
  } catch {
    // ignore
  }
  try {
    const text = await response.text();
    if (text) return text;
  } catch {
    // ignore
  }
  return fallback;
};

function GeneralLedgerTrialBalancePage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat(localeKey, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }), [localeKey]);

  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined) return currencyFormatter.format(0);
    const normalized = Number(value);
    if (Number.isNaN(normalized)) return currencyFormatter.format(0);
    return currencyFormatter.format(normalized);
  }, [currencyFormatter]);

  const formatCurrencyTL = useCallback((value) => `${formatCurrency(value)} TL`, [formatCurrency]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [sourceType, setSourceType] = useState('TB');
  const [accountCodeFilter, setAccountCodeFilter] = useState('');
  const [job, setJob] = useState(null);
  const [filteredSummary, setFilteredSummary] = useState(null);
  const [lines, setLines] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, size: 20, totalPages: 0, totalItems: 0 });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setPage(0);
  }, [selection.companyId, selection.periodId, sourceType, accountCodeFilter]);

  const fetchData = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) {
      setJob(null);
      setFilteredSummary(null);
      setLines([]);
      setPagination((prev) => ({ ...prev, totalPages: 0, totalItems: 0 }));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        companyId: selection.companyId,
        periodId: selection.periodId,
        sourceType,
        page,
        size: pagination.size
      });
      if (accountCodeFilter.trim()) params.set('accountCodeFilter', accountCodeFilter.trim());
      const response = await fetch(`${API_BASE_URL}/api/financials/kebir-mizan?${params}`, { headers: buildHeaders() });
      if (!response.ok) {
        const errorMessage = await parseErrorMessage(response, t('fetchFailed'), t);
        throw new Error(errorMessage);
      }

      const payload = await response.json().catch(() => ({}));
      setJob(payload.job || null);
      setFilteredSummary(payload.filteredSummary || null);
      setLines(payload.lines || []);

      const meta = payload.pagination || {};
      const nextPage = Number.isFinite(meta.page) ? meta.page : page;
      const nextSize = meta.size || pagination.size;
      setPagination({
        page: nextPage,
        size: nextSize,
        totalPages: meta.totalPages || 0,
        totalItems: meta.totalItems || 0
      });
      setPage(nextPage);
    } catch (caught) {
      setJob(null);
      setFilteredSummary(null);
      setLines([]);
      setPagination((prev) => ({ ...prev, totalPages: 0, totalItems: 0 }));
      setError(caught?.message || t('fetchError'));
    } finally {
      setLoading(false);
    }
  }, [selection.companyId, selection.periodId, sourceType, page, pagination.size, accountCodeFilter, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerate = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) return;

    setProcessing(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/financials/kebir-mizan/jobs`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ companyId: selection.companyId, periodId: selection.periodId, sourceType })
      });
      if (!response.ok) {
        const errorMessage = await parseErrorMessage(response, t('generateFailed'), t);
        throw new Error(errorMessage);
      }

      const payload = await response.json().catch(() => ({}));
      setJob(payload);
      await fetchData();
    } catch (caught) {
      setError(caught?.message || t('generateFailed'));
    } finally {
      setProcessing(false);
    }
  }, [selection.companyId, selection.periodId, sourceType, fetchData, t]);

  const formatGeneratedAt = useMemo(() => {
    if (!job?.generatedAt) return '-';
    const date = new Date(job.generatedAt);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat(localeKey, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  }, [job, localeKey]);

  const headerDescription = useMemo(() => {
    const companyLabel = selection.company?.name || t('companyNotSelected');
    const periodLabel = selection.period?.code || selection.period?.description || t('periodNotSelected');
    return t('headerDescription').replace('{{company}}', companyLabel).replace('{{period}}', periodLabel);
  }, [selection.company, selection.period, t]);

  const isFiltered = Boolean(accountCodeFilter.trim()) && filteredSummary !== null;
  const totalDebit = isFiltered ? Number(filteredSummary?.totalDebit ?? 0) : Number(job?.totalDebit ?? 0);
  const totalCredit = isFiltered ? Number(filteredSummary?.totalCredit ?? 0) : Number(job?.totalCredit ?? 0);
  const diffValue = totalDebit - totalCredit;
  const absDiff = Math.abs(diffValue);
  const isBalanced = absDiff < 0.01;
  const balanceScore = Math.max(0, Math.round(100 - ((absDiff / Math.max(Math.abs(totalDebit), 1)) * 1000)));

  const handlePreviousPage = () => {
    if (pagination.page <= 0) return;
    setPage((prev) => Math.max(prev - 1, 0));
  };

  const handleNextPage = () => {
    if (pagination.page >= pagination.totalPages - 1) return;
    setPage((prev) => prev + 1);
  };

  const [exporting, setExporting] = useState(false);

  const fetchAllLinesForExport = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) return [];
    const allLines = [];
    let pageNum = 0;
    const pageSize = 500;
    let hasMore = true;
    while (hasMore) {
      const params = new URLSearchParams({
        companyId: selection.companyId,
        periodId: selection.periodId,
        sourceType,
        page: pageNum,
        size: pageSize
      });
      if (accountCodeFilter.trim()) params.set('accountCodeFilter', accountCodeFilter.trim());
      const response = await fetch(`${API_BASE_URL}/api/financials/kebir-mizan?${params}`, { headers: buildHeaders() });
      if (!response.ok) return [];
      const payload = await response.json().catch(() => ({}));
      const pageLines = payload.lines || [];
      allLines.push(...pageLines);
      const meta = payload.pagination || {};
      const totalPages = meta.totalPages || 0;
      hasMore = pageNum + 1 < totalPages && pageLines.length === pageSize;
      pageNum += 1;
    }
    return allLines;
  }, [selection.companyId, selection.periodId, sourceType, accountCodeFilter]);

  const exportFilename = useMemo(() => {
    const company = selection.company?.name?.replace(/[^a-zA-Z0-9\u00C0-\u024F]/g, '_') || 'company';
    const period = selection.period?.code || selection.period?.description?.replace(/[^a-zA-Z0-9]/g, '_') || 'period';
    return `kebir_mizan_${company}_${period}`;
  }, [selection.company, selection.period]);

  const exportHeaderText = useMemo(() => {
    const companyLabel = selection.company?.name || t('companyNotSelected');
    const periodLabel = selection.period?.code || selection.period?.description || t('periodNotSelected');
    return t('exportHeader').replace('{{company}}', companyLabel).replace('{{period}}', periodLabel);
  }, [selection.company, selection.period, t]);

  const handleExportExcel = useCallback(async () => {
    setExporting(true);
    try {
      const allLines = await fetchAllLinesForExport();
      if (allLines.length) exportToExcel(allLines, exportFilename);
    } finally {
      setExporting(false);
    }
  }, [fetchAllLinesForExport, exportFilename]);

  const handleExportPdf = useCallback(async () => {
    setExporting(true);
    try {
      const allLines = await fetchAllLinesForExport();
      if (allLines.length) exportToPdf(allLines, exportFilename, exportHeaderText);
    } finally {
      setExporting(false);
    }
  }, [fetchAllLinesForExport, exportFilename, exportHeaderText]);

  const readyToExport = Boolean(job && lines.length > 0);

  const sourceOptions = useMemo(() => ([
    { value: 'TB', label: t('sourceTB') },
    { value: 'JOURNAL', label: t('sourceJournal') },
    { value: 'GELIR_TABLOSU', label: t('sourceIncome') },
    { value: 'CORRECTED_MIZAN', label: t('sourceCorrected') }
  ]), [t]);

  return (
    <div className="kebir-mizan">
      <PageHeader
        title={t('headerTitle')}
        description={headerDescription}
        actions={<div className="kebir-mizan__header-actions"><span className="kebir-mizan__badge">{sourceType}</span></div>}
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      <section className="kebir-mizan__controls-card">
        <div className="kebir-mizan__source">
          <label htmlFor="kebir-source-type">{t('source')}</label>
          <select id="kebir-source-type" value={sourceType} onChange={(event) => setSourceType(event.target.value)} disabled={processing}>
            {sourceOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="kebir-mizan__filter">
          <label htmlFor="kebir-account-filter">{t('accountCodeFilterLabel')}</label>
          <input
            id="kebir-account-filter"
            type="text"
            value={accountCodeFilter}
            onChange={(event) => setAccountCodeFilter(event.target.value)}
            placeholder={t('accountCodeFilterPlaceholder')}
          />
        </div>

        <div className="kebir-mizan__actions">
          <button type="button" className="btn-primary" onClick={handleGenerate} disabled={!selection.companyId || processing}>
            {processing ? t('generating') : t('generateRefresh')}
          </button>
          <button type="button" className="btn-secondary" onClick={handleExportExcel} disabled={!readyToExport || exporting} title={t('exportExcelTitle')}>
            {exporting ? '...' : 'Excel'}
          </button>
          <button type="button" className="btn-secondary" onClick={handleExportPdf} disabled={!readyToExport || exporting} title={t('exportPdfTitle')}>
            {exporting ? '...' : 'PDF'}
          </button>
        </div>
      </section>

      <section className="kebir-mizan__status-grid">
        <article className="kebir-mizan__status-card">
          <p>{t('balanceScore')}</p>
          <strong>{balanceScore}</strong>
          <span className={`kebir-mizan__tag ${isBalanced ? 'ok' : 'risk'}`}>{isBalanced ? t('balanced') : t('hasDiff')}</span>
        </article>

        <article className="kebir-mizan__status-card">
          <p>{t('totalDebit')}{isFiltered && <span className="kebir-mizan__filtered-badge">{t('filteredLabel')}</span>}</p>
          <strong>{formatCurrencyTL(totalDebit)}</strong>
          <span>{t('debitTotalNote')}</span>
        </article>

        <article className="kebir-mizan__status-card">
          <p>{t('totalCredit')}{isFiltered && <span className="kebir-mizan__filtered-badge">{t('filteredLabel')}</span>}</p>
          <strong>{formatCurrencyTL(totalCredit)}</strong>
          <span>{t('creditTotalNote')}</span>
        </article>

        <article className={`kebir-mizan__status-card ${isBalanced ? '' : 'kebir-mizan__value--out-of-balance'}`}>
          <p>{t('diff')}{isFiltered && <span className="kebir-mizan__filtered-badge">{t('filteredLabel')}</span>}</p>
          <strong>{formatCurrencyTL(diffValue)}</strong>
          <span>{isBalanced ? t('trialEqual') : t('reconciliationRequired')}</span>
        </article>

        <article className="kebir-mizan__status-card">
          <p>{t('lastGeneration')}</p>
          <strong>{formatGeneratedAt}</strong>
          <span>{t('rowCount')}: {job?.rowCount ?? 0}</span>
        </article>
      </section>

      {error && <p className="kebir-mizan__error">{error}</p>}

      <section className="kebir-mizan__table-wrapper">
        <div className="kebir-mizan__table-header">
          <div>
            <h3>{t('outputTable')}</h3>
            <p className="muted">{t('rowsDisplayed').replace('{{count}}', String(lines.length))}</p>
          </div>
        </div>

        {loading ? (
          <div className="kebir-mizan__loading"><p>{t('loadingData')}</p></div>
        ) : lines.length === 0 ? (
          <div className="kebir-mizan__empty"><p>{t('emptyData')}</p></div>
        ) : (
          <div className="kebir-mizan__table-scroll">
            <table className="kebir-mizan__table">
              <thead>
                <tr>
                  <th>{t('colKebirCode')}</th>
                  <th>{t('colKebirName')}</th>
                  <th className="kebir-mizan__align-right">{t('colDebit')}</th>
                  <th className="kebir-mizan__align-right">{t('colCredit')}</th>
                  <th className="kebir-mizan__align-right">{t('colBalance')}</th>
                  <th className="kebir-mizan__align-right">{t('colRows')}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={`${line.kebirCode}-${index}`}>
                    <td className="kebir-mizan__account">{line.kebirCode}</td>
                    <td>{line.kebirName}</td>
                    <td className="kebir-mizan__align-right">{formatCurrency(line.debitTotal)}</td>
                    <td className="kebir-mizan__align-right">{formatCurrency(line.creditTotal)}</td>
                    <td className="kebir-mizan__align-right">{formatCurrency(line.balance)}</td>
                    <td className="kebir-mizan__align-right">{line.lineCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="kebir-mizan__pagination">
        <button type="button" className="btn-secondary kebir-mizan__pager-btn" onClick={handlePreviousPage} disabled={pagination.page <= 0}>{t('prev')}</button>

        <span>{t('pageSummary').replace('{{page}}', String(pagination.page + 1)).replace('{{totalPages}}', String(Math.max(pagination.totalPages, 1))).replace('{{totalItems}}', String(pagination.totalItems))}</span>

        <button type="button" className="btn-secondary kebir-mizan__pager-btn" onClick={handleNextPage} disabled={pagination.page >= pagination.totalPages - 1}>{t('next')}</button>
      </div>
    </div>
  );
}

export default GeneralLedgerTrialBalancePage;
